import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { GoogleOutlined, CheckCircleFilled } from '@ant-design/icons';
import { TWButton, TWInput, ToggleSwitch } from '../../../components/ui';
import companySettingApi from '../../../services/companySettingApi';
import { fetchCompanySettings } from '../../../store/slices/companySlice';
import { splitSettingsForSave } from '../../preferences/order-notification-settings';
import {
  GOOGLE_ADS_CODES,
  canEnableGoogleAds,
  isValidConversionId,
  isValidConversionLabel,
  normalizeConversionId,
  readGoogleAdsSettings,
} from './google-ads-settings';

export const GoogleAdsIntegrationHeader = ({ globalCompany }) => {
  const settingsMap = useSelector((state) => state.companyReducer.settings);
  const stored = readGoogleAdsSettings(settingsMap);
  const live = Boolean(
    globalCompany?.id && stored.enabled && canEnableGoogleAds(stored),
  );

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center">
        <span className="pl-5 text-[28px] text-[#4285F4]">
          <GoogleOutlined />
        </span>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-900 dark:text-slate-400">
            Google Ads
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-500">
            Conversion tracking for your web ordering site
          </div>
        </div>
      </div>
      <div className="pr-5">
        {live ? (
          <CheckCircleFilled style={{ fontSize: '28px', color: '#52c41a' }} />
        ) : null}
      </div>
    </div>
  );
};

// A bare string body (e.g. "These settings already exist") or { message }.
const saveErrorText = (error) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  return data?.message || 'Failed to save Google Ads settings';
};

const GoogleAdsIntegration = ({ globalCompany }) => {
  const dispatch = useDispatch();
  const settingsMap = useSelector((state) => state.companyReducer.settings);
  const stored = useMemo(
    () => readGoogleAdsSettings(settingsMap),
    [settingsMap],
  );

  const [enabled, setEnabled] = useState(stored.enabled);
  const [conversionId, setConversionId] = useState(stored.conversionId);
  const [conversionLabel, setConversionLabel] = useState(stored.conversionLabel);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (globalCompany?.id) {
      dispatch(fetchCompanySettings(globalCompany.id));
    }
  }, [dispatch, globalCompany?.id]);

  useEffect(() => {
    setEnabled(stored.enabled);
    setConversionId(stored.conversionId);
    setConversionLabel(stored.conversionLabel);
  }, [stored.enabled, stored.conversionId, stored.conversionLabel]);

  const idError =
    conversionId.trim() && !isValidConversionId(conversionId)
      ? 'Use an ID like AW-123456789'
      : '';
  const labelError =
    conversionLabel.trim() && !isValidConversionLabel(conversionLabel)
      ? 'Use the conversion label from Google Ads'
      : '';
  const enableBlocked = enabled && !canEnableGoogleAds({ conversionId, conversionLabel });

  const fieldsInvalid = Boolean(idError || labelError);

  const onSave = async () => {
    if (!globalCompany?.id) return;
    if (enableBlocked) {
      message.error('Add a valid Conversion ID and label before enabling.');
      return;
    }

    // Off with a typo in a field: turn tracking off and leave the stored
    // ID and label as they are (audit 2026-10-11 F10).
    const changes =
      !enabled && fieldsInvalid
        ? { [GOOGLE_ADS_CODES.ENABLED]: 'false' }
        : {
            [GOOGLE_ADS_CODES.ENABLED]: enabled ? 'true' : 'false',
            [GOOGLE_ADS_CODES.CONVERSION_ID]:
              normalizeConversionId(conversionId) || conversionId.trim(),
            [GOOGLE_ADS_CODES.CONVERSION_LABEL]: conversionLabel.trim(),
          };

    setSaving(true);
    try {
      const { toCreate, toUpdate } = splitSettingsForSave(settingsMap, changes);
      if (toCreate.length > 0) {
        await companySettingApi.batchCreate({
          companyid: globalCompany.id,
          settings: toCreate,
        });
      }
      if (toUpdate.length > 0) {
        await companySettingApi.batchUpdate({
          companyid: globalCompany.id,
          settings: toUpdate,
        });
      }
      message.success('Google Ads settings saved');
    } catch (error) {
      message.error(saveErrorText(error));
    } finally {
      // Same as preferences.js: refetch even after a failed write, so a
      // partial batch never routes the next save to Create for a row that
      // now exists; a refetch failure alone never repaints a successful
      // save as an error.
      try {
        await dispatch(fetchCompanySettings(globalCompany.id)).unwrap();
      } catch (error) {
        // keep the settings map as-is; the next save retries the fetch
      }
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 px-2 py-2 sm:px-4">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {'Measure whether Google Ads clicks become orders on your web ordering site. Paste the Conversion ID and label from Google Ads. The ordering site sends a purchase conversion after each paid web order. Kiosk and mobile app orders are not tracked.'}
      </p>

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            Enable conversion tracking
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Loads Google tag on this company&apos;s order site and fires a
            conversion on the thank-you page.
          </p>
        </div>
        <ToggleSwitch
          checked={enabled}
          disabled={saving || !globalCompany?.id}
          ariaLabel="Enable Google Ads conversion tracking"
          onChange={setEnabled}
        />
      </div>

      <TWInput
        label="Conversion ID"
        placeholder="AW-123456789"
        value={conversionId}
        error={idError}
        disabled={saving || !globalCompany?.id}
        onChange={(e) => setConversionId(e.target.value)}
      />
      <TWInput
        label="Conversion label"
        placeholder="Purchase label from Google Ads"
        value={conversionLabel}
        error={labelError}
        disabled={saving || !globalCompany?.id}
        onChange={(e) => setConversionLabel(e.target.value)}
      />

      {enableBlocked ? (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Tracking stays off until both fields are valid.
        </p>
      ) : null}

      <div className="flex justify-end">
        <TWButton
          variant="primary"
          label="Save"
          loading={saving}
          disabled={saving || !globalCompany?.id || (enabled && fieldsInvalid)}
          onClick={onSave}
        />
      </div>
    </div>
  );
};

export default GoogleAdsIntegration;
